/**
 * /mi-acceso — ver `MEMBERSHIP_ROUTE.MI_ACCESO`
 *
 * Usuarios aprobados: muestra el link privado de ingreso (p. ej. Telegram).
 * Sin aprobación → `MEMBERSHIP_ROUTE.PENDING_APPROVAL`.
 * Sin sesión → `MEMBERSHIP_ROUTE.REGISTRO`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
import { getUserDisplayName, isClerkAdmin } from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_SUPPORT_EMAIL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { trackLinkClickPostHog } from "@/lib/posthogAnalytics";
import { useClerk, useUser } from "@clerk/clerk-react";
import { CheckCircle2, Copy, ExternalLink, Loader2, LogOut, Mail, MessageCircle, Sparkles } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

const SEO = {
  title: `Tu acceso a la membresía — ${BRAND_PERSON_NAME}`,
  description: `Link de ingreso privado asignado por el equipo tras aprobar tu membresía.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.MI_ACCESO}`,
  robots: "noindex, nofollow" as const,
};

export default function MiAcceso() {
  useSEO(SEO);
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!user) return <Navigate to={MEMBERSHIP_ROUTE.REGISTRO} replace />;
  if (isClerkAdmin(user)) return <Navigate to="/admin" replace />;

  const approved = user.publicMetadata?.approved === true;
  const telegramUrl =
    (user.publicMetadata?.telegram_invite_url as string | undefined)?.trim() ||
    null;
  // VIP is assigned at payment, but the private invite is only shown after
  // Clerk registration + admin approval with a real telegram_invite_url.
  if (!approved || !telegramUrl) {
    return <Navigate to={MEMBERSHIP_ROUTE.PENDING_APPROVAL} replace />;
  }
  const displayName = getUserDisplayName(user);
  const email = user.primaryEmailAddress?.emailAddress ?? "";

  const copy = async () => {
    if (!telegramUrl) return;
    try {
      await navigator.clipboard.writeText(telegramUrl);
      setCopied(true);
      toast({ title: "Link copiado" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-8">
      <Card className="max-w-md w-full border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
        <CardHeader className="text-center space-y-3 pb-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-300" />
          </div>
          <CardTitle className="text-xl sm:text-2xl text-zinc-100 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-300" />
            ¡Bienvenido, {displayName}!
          </CardTitle>
          <CardDescription className="text-sm text-zinc-400">
            Tu membresía con {BRAND_PERSON_NAME} está aprobada. Acá tenés el link privado de ingreso.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {telegramUrl ? (
            <>
              <div className="rounded-lg bg-[#0088cc]/10 border border-[#0088cc]/30 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="w-5 h-5 text-[#0088cc]" />
                  <span className="font-semibold text-zinc-100">Link privado de Telegram</span>
                </div>
                <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 border border-white/5">
                  <code className="flex-1 text-xs text-zinc-300 font-mono truncate">{telegramUrl}</code>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={copy}
                    className="text-zinc-400 hover:text-zinc-100 hover:bg-white/10 shrink-0"
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <Button
                asChild
                className="w-full h-12 bg-[#0088cc] hover:bg-[#0099d8] text-white font-semibold gap-2 text-base"
              >
                <a
                  href={telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackLinkClickPostHog("mi_acceso_join_group", telegramUrl)
                  }
                >
                  <MessageCircle className="w-5 h-5" />
                  Unirme al grupo
                  <ExternalLink className="w-4 h-4 opacity-80" />
                </a>
              </Button>

              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-200/90 leading-relaxed">
                ⚠️ <strong className="text-amber-100">Este link es personal.</strong> No lo compartas — el equipo
                puede revocar el acceso si detectamos uso indebido.
              </div>
            </>
          ) : (
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-4 text-sm text-rose-200">
              Estás aprobado pero el link de Telegram aún no está cargado. Volvé a esta página en unos minutos
              o avisanos a{" "}
              <a href={`mailto:${BRAND_SUPPORT_EMAIL}`} className="underline underline-offset-2">
                {BRAND_SUPPORT_EMAIL}
              </a>
              .
            </div>
          )}

          <p className="text-xs text-zinc-500 text-center">
            Conectado como <code className="rounded bg-black/40 px-1 text-zinc-400">{email}</code>
          </p>

          <MembershipPortalHint />

          <div className="pt-2 border-t border-white/5 flex items-center justify-between text-xs">
            <a
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
              className="text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
            >
              <Mail className="w-3 h-3" />
              Contacto
            </a>
            <button
              onClick={() => signOut()}
              className="text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
            >
              <LogOut className="w-3 h-3" />
              Cerrar sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
